import React, { useEffect, useState } from "react";
import { ArrowRightLeft, X, Trash2, Wallet, ArrowDown, ArrowUp } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TREASURY_ACCOUNTS, accountLabel, accountColor } from "./financeConstants";
import { ConfirmModal } from "@/components/AppDialogs";
import { toast } from "sonner";

/**
 * Treasury overview: 5 fixed account balances + internal transfer ledger.
 *
 * Balance = inflows (subscriptions, contributions, transfers received)
 *         − outflows (approved expenses, transfers sent)
 *
 * Opening balance is 0 per product decision — everything is computed from txs.
 */
export default function TreasuryPanel({ canMutate, fmt }) {
  const [summary, setSummary] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteTransferFor, setDeleteTransferFor] = useState(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/treasury/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null)),
      api.get("/admin/treasury/transfers").then((r) => setTransfers(r.data)).catch(() => setTransfers([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const remove = (t) => setDeleteTransferFor(t);
  const performRemove = async () => {
    const t = deleteTransferFor;
    setDeleteTransferFor(null);
    if (!t) return;
    try {
      await api.delete(`/admin/treasury/transfers/${t.id}`);
      refresh();
      toast.success("Transfert supprimé.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="treasury-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl font-black text-[#2A0E3C]">Trésorerie</h2>
          <p className="text-xs text-[#5C4664] mt-0.5">
            Solde par compte = encaissements − dépenses approuvées − transferts émis.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="transfer-new-btn"
          className="rounded-full bg-[#6B2C8F] hover:bg-[#4F1F6B] text-white gap-2"
        >
          <ArrowRightLeft className="w-4 h-4" /> Nouveau transfert
        </Button>
      </div>

      {loading || !summary ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : (
        <>
          {/* Total */}
          <div data-testid="treasury-total" className="bg-[#2A0E3C] text-white rounded-2xl p-5 mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">
              Trésorerie consolidée Milkado
            </p>
            <p className="font-display text-4xl font-black mt-1">
              {fmt(summary.total_balance)}
            </p>
            {summary.pending_approval?.count > 0 && (
              <p className="text-xs text-amber-300 mt-2">
                ⚠ {summary.pending_approval.count} dépense(s) en attente d&apos;approbation —
                impact potentiel : <strong>−{fmt(summary.pending_approval.total)}</strong>
              </p>
            )}
          </div>

          {/* Per-account cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="treasury-accounts">
            {summary.accounts.map((a) => (
              <AccountCard key={a.account} account={a} fmt={fmt} />
            ))}
          </div>

          {/* Reports: by category + by supplier */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <ReportBlock
              title="Dépenses par catégorie"
              data={Object.entries(summary.expenses_by_category || {}).map(([k, v]) => ({ label: categoryFr(k), value: v }))}
              fmt={fmt}
              testid="report-by-category"
              empty="Aucune dépense approuvée pour le moment."
            />
            <ReportBlock
              title="Dépenses par fournisseur"
              data={(summary.expenses_by_supplier || []).map((s) => ({ label: s.supplier_name, value: s.total }))}
              fmt={fmt}
              testid="report-by-supplier"
              empty="Aucun achat fournisseur enregistré."
            />
          </div>

          {/* Transfers log */}
          <div className="bg-white rounded-2xl border border-[#F2E2EC] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F2E2EC] flex items-center justify-between">
              <h3 className="font-display font-black text-[#2A0E3C]">
                Transferts internes <span className="text-xs font-normal text-[#5C4664]">({transfers.length})</span>
              </h3>
            </div>
            {transfers.length === 0 ? (
              <p data-testid="transfers-empty" className="text-sm text-[#5C4664] p-6 text-center">
                Aucun transfert effectué.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="transfers-table">
                <thead className="bg-[#FCE7F3] text-[10px] uppercase tracking-widest text-[#5C4664]">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">De</th>
                    <th></th>
                    <th className="text-left px-3 py-2">Vers</th>
                    <th className="text-right px-3 py-2">Montant</th>
                    <th className="text-left px-3 py-2">Note</th>
                    {canMutate && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((t) => (
                    <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-t border-[#F2E2EC]">
                      <td className="px-3 py-2 text-xs text-[#5C4664] whitespace-nowrap">
                        {t.transferred_at ? new Date(t.transferred_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="font-semibold" style={{ color: accountColor(t.from_account) }}>
                          {accountLabel(t.from_account)}
                        </span>
                      </td>
                      <td className="px-1"><ArrowRightLeft className="w-3 h-3 text-[#5C4664]" /></td>
                      <td className="px-3 py-2 text-xs">
                        <span className="font-semibold" style={{ color: accountColor(t.to_account) }}>
                          {accountLabel(t.to_account)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-display font-black text-[#6B2C8F]">{fmt(t.amount)}</td>
                      <td className="px-3 py-2 text-xs text-[#5C4664]">{t.note || "—"}</td>
                      {canMutate && (
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => remove(t)}
                            data-testid={`transfer-delete-${t.id}`}
                            title="Supprimer (super_admin)"
                            className="w-7 h-7 rounded-full border border-[#F2E2EC] hover:bg-red-50 hover:border-red-300 text-[#5C4664] hover:text-red-600 inline-flex items-center justify-center"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {creating && (
        <TransferFormModal
          onCancel={() => setCreating(false)}
          onSubmit={async (payload) => {
            try {
              await api.post("/admin/treasury/transfers", payload);
              setCreating(false);
              refresh();
            } catch (err) {
              throw new Error(formatApiError(err.response?.data?.detail) || "Erreur");
            }
          }}
        />
      )}
      <ConfirmModal
        open={!!deleteTransferFor}
        onClose={() => setDeleteTransferFor(null)}
        onConfirm={performRemove}
        title="Supprimer ce transfert ?"
        description={deleteTransferFor ? `Transfert de $${deleteTransferFor.amount} ${accountLabel(deleteTransferFor.from_account)} → ${accountLabel(deleteTransferFor.to_account)}` : ""}
        confirmLabel="Supprimer"
        destructive
        testId="treasury-delete-transfer-confirm"
      />
    </div>
  );
}

function AccountCard({ account, fmt }) {
  const color = accountColor(account.account);
  const positive = account.balance >= 0;
  return (
    <div
      data-testid={`account-card-${account.account}`}
      className="bg-white rounded-2xl border-2 p-4"
      style={{ borderColor: `${color}33` }}
    >
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
          <Wallet className="w-4 h-4" />
        </div>
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color }}>
          {accountLabel(account.account)}
        </p>
      </div>
      <p
        className="font-display text-2xl font-black mt-2"
        style={{ color: positive ? color : "#B91C1C" }}
      >
        {fmt(account.balance)}
      </p>
      <div className="mt-3 pt-3 border-t border-[#F2E2EC] grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
        <span className="text-[#3FA535] flex items-center gap-1">
          <ArrowDown className="w-3 h-3" />Entrées
        </span>
        <span className="text-right font-bold text-[#3FA535]">
          +{fmt(account.inflow_subscriptions + account.inflow_contributions + account.inflow_transfers)}
        </span>
        <span className="text-[#B91C1C] flex items-center gap-1">
          <ArrowUp className="w-3 h-3" />Sorties
        </span>
        <span className="text-right font-bold text-[#B91C1C]">
          −{fmt(account.outflow_expenses + account.outflow_transfers)}
        </span>
      </div>
    </div>
  );
}

function ReportBlock({ title, data, fmt, testid, empty }) {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...sorted.map((d) => d.value));
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#F2E2EC] p-5">
      <h3 className="font-display font-black text-[#2A0E3C] mb-3">{title}</h3>
      {sorted.length === 0 ? (
        <p className="text-sm text-[#5C4664]">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((d) => (
            <li key={d.label}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#2A0E3C] font-semibold">{d.label}</span>
                <span className="font-display font-black text-[#D81B8C]">{fmt(d.value)}</span>
              </div>
              <div className="h-1.5 bg-[#F2E2EC] rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full bg-[#D81B8C]"
                  style={{ width: `${(d.value / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TransferFormModal({ onCancel, onSubmit }) {
  const [form, setForm] = useState({
    from_account: "cash_bureau",
    to_account: "banque",
    amount: "",
    note: "",
    transferred_at: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.from_account === form.to_account) {
      setError("Les comptes source et destination doivent être différents.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        from_account: form.from_account,
        to_account: form.to_account,
        amount: parseFloat(form.amount),
        note: form.note.trim(),
        transferred_at: form.transferred_at ? new Date(form.transferred_at).toISOString() : null,
      });
    } catch (err) {
      setError(err.message || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="transfer-form-modal" className="fixed inset-0 z-50 bg-[#2A0E3C]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-md p-6 space-y-3">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-black text-[#2A0E3C]">Transfert interne</h3>
          <button type="button" onClick={onCancel} className="text-[#5C4664]" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="De *">
            <select
              data-testid="transfer-form-from"
              value={form.from_account}
              onChange={(e) => setForm({ ...form, from_account: e.target.value })}
              className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
            >
              {TREASURY_ACCOUNTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Vers *">
            <select
              data-testid="transfer-form-to"
              value={form.to_account}
              onChange={(e) => setForm({ ...form, to_account: e.target.value })}
              className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
            >
              {TREASURY_ACCOUNTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Montant *">
            <Input
              data-testid="transfer-form-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              className="rounded-xl"
            />
          </Field>
          <Field label="Date">
            <Input
              data-testid="transfer-form-date"
              type="date"
              value={form.transferred_at}
              onChange={(e) => setForm({ ...form, transferred_at: e.target.value })}
              className="rounded-xl"
            />
          </Field>
        </div>

        <Field label="Note">
          <Input
            data-testid="transfer-form-note"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Ex: Dépôt hebdomadaire"
            className="rounded-xl"
          />
        </Field>

        {error && <p data-testid="transfer-form-error" className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button type="button" onClick={onCancel} variant="outline" className="flex-1 rounded-full border-[#F2E2EC]">
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="transfer-form-submit"
            className="flex-1 rounded-full bg-[#6B2C8F] hover:bg-[#4F1F6B] text-white disabled:opacity-50"
          >
            {submitting ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

// Local helper — keeps the panel self-contained without importing the
// whole category constants list for just one mapping.
function categoryFr(id) {
  return ({
    supplier_purchase: "Achat fournisseur",
    salaries: "Salaires",
    rent: "Loyer",
    transport_logistics: "Transport & logistique",
    marketing: "Marketing",
    other: "Divers",
  })[id] || id;
}
